import React from 'react';
import Cookies from 'js-cookie';

import { InputTool } from './components/InputTool/InputTool';
import { GuessScoreColumn } from './components/GuessScoreColumn/GuessScoreColumn';
import { DropDownMenu } from './components/DropDownMenu/DropDownMenu';
import { EndModal } from './components/PopUps/EndModal/EndModal';
import InitAnalytics, { TrackGameWon } from './util/GoogleAnalytics';

interface GameState {
  randomized: boolean;
  over: boolean;
  data: GameData;
  endModalOpen: boolean;
}

export type GameData = {
  code: string[];
  guesses: string[][];
  scores: string[][];
};

export class MastermindApp extends React.Component<{}, GameState>{
  private date: Date = new Date();
  private day: number = this.date.getDate();
  private month: number = this.date.getMonth() + 1;
  private year: number = this.date.getFullYear();
  private dateString: string = `${this.month}/${this.day}/${this.year}`;
  private seedrandom = require('seedrandom');


  constructor(props: {}) {
    super(props);
    this.state = { randomized: false, over: false, data: {code: this.generateCodeByDate(), guesses: [], scores: []}, endModalOpen: false};
    this.addGuess = this.addGuess.bind(this);
    this.playAgain = this.playAgain.bind(this);
  }

  componentDidMount(): void {
    document.title = 'Codecrackr';
    const dateGuessesCookiesJSON: string | undefined =  Cookies.get(this.dateString+'guesses');
    const dateScoresCookiesJSON: string | undefined = Cookies.get(this.dateString+'scores')
    if(this.state.randomized) return;
    if(!dateGuessesCookiesJSON || !dateScoresCookiesJSON) return;
    const guesses: string[][] = JSON.parse(dateGuessesCookiesJSON);
    const scores: string[][] = JSON.parse(dateScoresCookiesJSON);

    InitAnalytics();

    this.setState( (prevState) => {
      const over = this.areEqual(guesses[guesses.length-1], prevState.data.code) || guesses.length === 8;
      
      return {
        randomized: prevState.randomized,
        over: over,
        data: {code: prevState.data.code, guesses: guesses, scores: scores},
        endModalOpen: over,
      };
    });
  }

  playAgain(): void{
    this.setState({
      randomized: true, 
      over: false,
      data: {code: this.generateRandomCode(), guesses: [], scores: []},
      endModalOpen: false
    })
  }

  generateCodeByDate(): string[] {
    const colorPalette = ['Red', 'Blue', 'Yellow', 'Green', 'Black', 'White'];
    var rng = this.seedrandom(this.dateString)

    const shuffleArray = (array: string[]): string[] => {
      for (let i = array.length - 1; i > 0; i--) {
        const j = Math.floor(rng.quick() * (i + 1));
        const tmp = array[i];
        array[i] = array[j];
        array[j] = tmp;
      }
      return array;
    };
    const newArr = shuffleArray(colorPalette).slice(0,4);
    return newArr;
  }

  generateRandomCode(): string[]{
    const colorPalette = ['Red', 'Blue', 'Yellow', 'Green', 'Black', 'White'];

    const shuffleArray = (array: string[]): string[] => {
      for (let i = array.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        const tmp = array[i];
        array[i] = array[j];
        array[j] = tmp;
      }
      return array;
    };

    const newArr = shuffleArray(colorPalette).slice(0,4);
    return newArr;
  }

  end(data: GameData){
    this.setState( (prevState) => {
      return {
        randomized: prevState.randomized,
        over: true,
        data: data,
        endModalOpen: true,
      }
    })
  }

  addGuess(guess: string[]){
    const over = this.areEqual(guess, this.state.data.code) || this.state.data.guesses.length === 7;
    const won = over && this.areEqual(guess, this.state.data.code)

    if(over){
      TrackGameWon(won, this.state.data.guesses.length + 1);
    }

    const codeSet = [...this.state.data.guesses];
    codeSet.push(guess);

    const scoreSet = [...this.state.data.scores];
    scoreSet.push(this.scoreGuess(this.state.data.code, guess)); 
    
    if(!this.state.randomized){
      Cookies.set(this.dateString+'guesses', JSON.stringify(codeSet));
      Cookies.set(this.dateString+'scores', JSON.stringify(scoreSet));
    }
    
    this.setState( (prevState) => {
      const newData = {code: prevState.data.code, guesses: codeSet, scores: scoreSet};
      return {
        randomized: prevState.randomized,
        over: over,
        data: newData,
        endModalOpen: over,
      }
    })
  }

  areEqual(guess: string[], code: string[]){
    return guess.every( (value, index) => value === code[index]);
  }

  closeEndModal(): void{
    this.setState((prevState)=>{
      return{
      ...prevState,
      endModalOpen: false
      };
    });
  }

  scoreGuess(code: string[], guess: string[]): string[]{
    const markedHits: boolean[] = [false, false, false, false];
    const score: string[] = [];
    let hits: number = 0;
    let misses: number = 0;
    let blows: number = 0;
    
    for (let i = 0; i < code.length; i++) {
      if (guess[i] === code[i]) {
        markedHits[i] = true;
        hits++;
      }
    }

    for (let i = 0; i < code.length; i++) {
      if(markedHits[i]) continue;
      if (guess.every((value) => value !== code[i])) {
        misses++;
      }
    }
    
    blows = 4 - (hits + misses);

    for(let i  = 0; i < hits; i++){
      score.push('Green');
    }

    for(let i = 0; i < blows; i++){
      score.push('Yellow');
    } 

    for(let i = 0; i < misses; i++){
      score.push('Red');
    }

    return score;
  }

  render(): React.ReactNode {
    const { over, data, endModalOpen, randomized } = this.state;
    return (
      <div id='modalRoot' className="min-h-[100dvh] w-[100dvw] overflow-hidden text-onBackgroundTextColor bg-bodyBackground select-none">

        <div className='w-full h-[10dvh] border-b-[2px] border-cardsBackground flex justify-center items-center'>
          {/* Top Bar Area (TITLE AND SETTINGS) */}
          <h1 className='text-3xl md:text-5xl lg:text-6xl'> CodeCrackr </h1>
          <DropDownMenu/>
        </div>

        <div className='w-full h-[50dvh] flex justify-center py-2'>
          <div className='w-full md:w-[80%] lg:w-[60%] h-full flex justify-center'>
            {/* Game Score Area */}
            <GuessScoreColumn scores={data.scores} guesses={data.guesses}/>
          </div>
        </div>

        <div className='w-full h-[40dvh]'>
          {/* Input Area */}
          <InputTool disabled={over} submitCallback={this.addGuess}/>
        </div>
        {endModalOpen && <EndModal randomized = {randomized} code={data.code} guesses={data.guesses} scores={data.scores} playAgainCallback={() => this.playAgain()} closeCallback={() => this.closeEndModal()}></EndModal>}
      </div>
    );
  }
};
